// src/pages/OrdersPage.js
import React, { useContext, useEffect, useState } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../services/api";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";

function OrdersPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const data = await apiRequest("/orders/my");
        setOrders(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  const TEXT = {
    EN: {
      mainPage: "Main page",
      account: "Account",
      title: "MY ORDERS",
      noOrders: "You have no previous orders.",
      status: "Status",
      total: "Total",
      items: "Items",
      loading: "Loading orders...",
      order: "Order",
      date: "Date",
      startShopping: "Start shopping",
backToAccount: "Back to account",
noItems: "No items found for this order.",
      coupon: "Coupon",
      quantity: "Quantity",
    },
    AR: {
      mainPage: "الصفحة الرئيسية",
      account: "الحساب",
      title: "طلباتي",
      startShopping: "ابدأ التسوق",
backToAccount: "العودة إلى الحساب",
noItems: "لا توجد منتجات لهذا الطلب.",
      noOrders: "لا يوجد طلبات سابقة.",
      status: "الحالة",
      total: "الإجمالي",
      items: "المنتجات",
      loading: "جاري تحميل الطلبات...",
      order: "الطلب",
      date: "التاريخ",
      coupon: "الكوبون",
      quantity: "الكمية",
    },
  };

  const t = TEXT[lang];

  const formatDate = (date) =>
    new Date(date).toLocaleDateString(lang === "AR" ? "ar" : "en", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    const OrdersPageSkeleton = () => (
  <div
    className="w-full min-h-screen bg-white overflow-x-hidden"
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <TopBanner />
    <MainHeader />

    <main className="bg-white px-4 md:px-8 md:pt-[175px]">
      <div className="h-4 w-[250px] bg-gray-200 animate-pulse rounded mb-6" />
      <div className="h-6 w-[150px] bg-gray-200 animate-pulse rounded mb-3" />
      <div className="h-[1px] bg-gray-200 mx-1 md:mx-4 mb-8" />

      <div className="max-w-5xl mx-auto space-y-5 pb-12">
        {[1, 2, 3].map((card) => (
          <div
            key={card}
            className="
              bg-white
              border border-gray-200
              rounded-xl
              p-5 md:p-6
              shadow-[0_10px_24px_rgba(0,0,0,0.05)]
            "
          >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
              <div>
                <div className="h-3 w-[70px] bg-gray-200 animate-pulse rounded mb-2" />
                <div className="h-6 w-[160px] bg-gray-200 animate-pulse rounded" />
              </div>

              <div>
                <div className="h-3 w-[70px] bg-gray-200 animate-pulse rounded mb-2 md:ml-auto" />
                <div className="h-4 w-[140px] bg-gray-200 animate-pulse rounded" />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 border-y border-gray-100 py-4 mb-5">
              {[1, 2, 3].map((i) => (
                <div key={i}>
                  <div className="h-3 w-[60px] bg-gray-200 animate-pulse rounded mb-2" />
                  <div className="h-4 w-[90px] bg-gray-200 animate-pulse rounded" />
                </div>
              ))}
            </div>

            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="
                    flex items-center gap-3
                    border border-gray-100
                    rounded-lg
                    p-3
                    bg-white
                  "
                >
                  <div className="w-14 h-14 bg-gray-200 animate-pulse flex-shrink-0" />
                  <div className="flex-1">
                    <div className="h-4 w-[70%] bg-gray-200 animate-pulse rounded" />
                    <div className="mt-2 h-3 w-[40%] bg-gray-200 animate-pulse rounded" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </main>

    <Footer />
  </div>
);

const OrdersEmptyState = () => (
  <div className="max-w-3xl mx-auto text-center mt-12 md:mt-16 border border-gray-200 bg-white rounded-2xl p-8 shadow-[0_10px_24px_rgba(0,0,0,0.04)]">
    <div
      className="
        mx-auto
        w-[60px] h-[60px]
        rounded-full
        border border-black
        flex items-center justify-center
        text-[30px]
        text-black
        mb-5
      "
    >
      !
    </div>

    <h2 className="text-[24px] md:text-[30px] font-bold text-black">
      {t.noOrders}
    </h2>

    <p className="mt-3 text-[14px] md:text-[15px] leading-7 text-gray-600">
      {lang === "AR"
        ? "عند إتمام أول طلب، ستظهر تفاصيله هنا."
        : "Once you place your first order, its details will appear here."}
    </p>

    <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
      <button
        type="button"
        onClick={() => navigate("/all-products")}
        className="
          w-full sm:w-[190px]
          h-11
          border-2 border-black
          rounded-md
          bg-black
          text-white
          text-sm font-semibold tracking-wide
          hover:opacity-90
          transition
        "
      >
        {t.startShopping}
      </button>

      <button
        type="button"
        onClick={() => navigate("/account")}
        className="
          w-full sm:w-[190px]
          h-11
          border-2 border-black
          rounded-md
          bg-white
          text-black
          text-sm font-semibold tracking-wide
          hover:bg-black hover:text-white
          transition
        "
      >
        {t.backToAccount}
      </button>
    </div>
  </div>
);

if (loading) {
  return <OrdersPageSkeleton />;
}

  return (
    <div
      className="w-full min-h-screen bg-white overflow-x-hidden"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <TopBanner />
      <MainHeader />

      <main className="bg-white px-4 md:px-8 md:pt-[175px]">
        {/* BREADCRUMB */}
        <div className="text-[12px] md:text-sm text-gray-900 mb-6">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="hover:text-black hover:underline"
          >
            {t.mainPage}
          </button>

          <span className="mx-2">›</span>

          <button
            type="button"
            onClick={() => navigate("/account")}
            className="hover:text-black hover:underline"
          >
            {t.account}
          </button>

          <span className="mx-2">›</span>

          <span className="text-gray-900">{t.title}</span>
        </div>

        {/* TITLE */}
        <div className="text-left mb-2">
          <h1 className="text-[16px] md:text-[20px] font-normal tracking-wide text-gray-900 uppercase">
            {t.title}
          </h1>
        </div>

        {/* LINE */}
        <div className="h-[1px] bg-gray-300 mx-1 md:mx-4 mb-8" />

        {/* EMPTY */}
{orders.length === 0 && <OrdersEmptyState />}

        {/* ORDERS */}
{orders.length > 0 && (
          <div className="max-w-5xl mx-auto space-y-5 pb-12">
            {orders.map((order) => (
              <div
                key={order.id}
                className="
                  bg-white
                  border border-gray-200
                  rounded-xl
                  p-5 md:p-6
                  shadow-[0_10px_24px_rgba(0,0,0,0.05)]
                "
              >
                {/* ORDER HEADER */}
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      {t.order}
                    </p>

                    <h2 className="text-[18px] md:text-[20px] font-bold text-black">
                      {order.order_number}
                    </h2>
                  </div>

                  <div className={lang === "AR" ? "md:text-left" : "md:text-right"}>
                    <p className="text-xs text-gray-500 mb-1">
                      {t.date}
                    </p>

                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(order.created_at)}
                    </p>
                  </div>
                </div>

                {/* SUMMARY */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 border-y border-gray-100 py-4 mb-5">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      {t.status}
                    </p>

                    <p
                      className={`
                        text-sm font-bold capitalize
                        ${
                          order.status === "delivered"
                            ? "text-green-700"
                            : order.status === "pending"
                            ? "text-orange-600"
                            : "text-gray-800"
                        }
                      `}
                    >
                      {order.status}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      {t.items}
                    </p>

                    <p className="text-sm font-bold text-black">
                      {order.total_items}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      {t.total}
                    </p>

                    <p className="text-sm font-bold text-black">
                      ${order.total_amount}
                    </p>
                  </div>
                </div>

                {/* COUPON */}
                {order.coupon_code && (
                  <div className="mb-5 inline-flex border border-gray-300 rounded-md px-3 py-1 text-xs text-gray-800">
                    {t.coupon}: {order.coupon_code}
                  </div>
                )}

                {/* ITEMS */}
<div className="space-y-3">
  {Array.isArray(order.items) && order.items.length > 0 ? (
    order.items.map((item, idx) => (
                    <div
                      key={idx}
                      className="
                        flex items-center gap-3
                        border border-gray-100
                        rounded-lg
                        p-3
                        bg-white
                      "
                    >
                      <div className="w-14 h-14 bg-gray-100 overflow-hidden flex-shrink-0">
                        <img
  src={item.image || "logo-512x512.png"}
  alt=""
  className="w-full h-full object-cover"
/>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-black leading-tight">
                          {lang === "EN"
                            ? item.product_name_en
                            : item.product_name_ar}
                        </p>

                        {item.public_id && (
                          <p className="text-[11px] text-gray-500 mt-1">
                            {item.public_id}
                          </p>
                        )}

                        <p className="text-xs text-gray-500 mt-1">
                          {t.quantity}: {item.qty}
                        </p>
                      </div>
                    </div>
                      ))
  ) : (
    <div className="border border-gray-100 rounded-lg p-3 bg-white text-sm text-gray-500">
      {t.noItems}
    </div>
  )}
</div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default OrdersPage;
